import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Checkbox } from "../ui/checkbox";

export function Pagination({ page, pageSize, count, onPage }) {
  const pages = Math.max(1, Math.ceil(count / pageSize));
  return (
    <div className="flex items-center justify-between gap-3 px-1 py-3 text-xs text-[#8C6A6C]">
      <span data-testid="table-count">{count} record(s)</span>
      <div className="flex items-center gap-2">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="table-prev-page" className="rounded-full border border-[#D4AF37]/50 p-1.5 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
        <span data-testid="table-page">Page {page} of {pages}</span>
        <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="table-next-page" className="rounded-full border border-[#D4AF37]/50 p-1.5 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export function DataTable({ columns, rows, loading, selectable, selected, onSelect, sort, onSort, rowActions, onRow }) {
  const allChecked = rows.length > 0 && rows.every((r) => selected?.has(r.id));
  const toggleAll = () => {
    const next = new Set(selected);
    rows.forEach((r) => (allChecked ? next.delete(r.id) : next.add(r.id)));
    onSelect(next);
  };
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelect(next);
  };
  return (
    <div className="overflow-x-auto rounded-xl border border-[#D4AF37]/30 bg-white">
      <table className="w-full min-w-[760px] text-left text-sm" data-testid="data-table">
        <thead className="bg-[#F4EFEA] text-xs uppercase tracking-wider text-[#8C6A6C]">
          <tr>
            {selectable && <th className="w-10 px-3 py-3"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="table-select-all" /></th>}
            {columns.map((c) => (
              <th key={c.key} className="whitespace-nowrap px-3 py-3 font-semibold">
                {c.sortable ? (
                  <button type="button" onClick={() => onSort({ key: c.key, dir: sort.key === c.key && sort.dir === "asc" ? "desc" : "asc" })} data-testid={`sort-${c.key}`} className="inline-flex items-center gap-1 hover:text-[#7A0016]">
                    {c.label}
                    {sort.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                  </button>
                ) : c.label}
              </th>
            ))}
            {rowActions && <th className="px-3 py-3 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length + 2} className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-[#7A0016]" /></td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={columns.length + 2} data-testid="table-empty" className="py-10 text-center text-sm text-[#8C6A6C]">No records found.</td></tr>
          ) : rows.map((r) => (
            <tr key={r.id} data-testid={`table-row-${r.id}`} onClick={onRow ? () => onRow(r) : undefined} className={`border-t border-[#D4AF37]/15 hover:bg-[#FAFAF7] ${onRow ? "cursor-pointer" : ""}`}>
              {selectable && <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`table-select-${r.id}`} /></td>}
              {columns.map((c) => <td key={c.key} className="whitespace-nowrap px-3 py-2.5 text-[#1A1515]">{c.render ? c.render(r) : r[c.key] ?? "-"}</td>)}
              {rowActions && <td className="whitespace-nowrap px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>{rowActions(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
